import { useEffect, useState } from "react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Star } from "lucide-react";
import api, { BACKEND_URL, formatApiErrorDetail } from "../../lib/api";
import { logger } from "../../lib/logger";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const resolveImg = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);
const PLACEHOLDER = "https://images.unsplash.com/photo-1500336624523-d727130c3328?w=120";

function SortableRow({ item, kind, onTogglePin }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const title = item.title || item.name;
  const sub = item.title
    ? `${item.artist_name || ""}${item.price ? ` · $${(item.price || 0).toLocaleString()}` : ""}`
    : item.country || "—";
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`ordering-row-${kind}-${item.id}`}
      className={`border bg-white flex items-center gap-3 p-2 ${
        item.pinned ? "border-[#F4C430] bg-[#FFFBEF]" : "border-[#E5E0D8]"
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        data-testid={`ordering-drag-${kind}-${item.id}`}
        className="cursor-grab active:cursor-grabbing text-[#555] hover:text-[#111] p-1 flex-shrink-0"
      >
        <GripVertical className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <img
        src={resolveImg(item.image_url || item.avatar_url) || PLACEHOLDER}
        alt=""
        className="w-12 h-12 object-cover bg-[#F4EFE6] flex-shrink-0"
        onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
      />
      <div className="flex-1 min-w-0">
        <p className="font-serif-display text-sm text-[#111] truncate">{title}</p>
        <p className="font-mono-aam text-xs text-[#555] mt-0.5 truncate">{sub}</p>
      </div>
      <button
        type="button"
        onClick={() => onTogglePin(item.id)}
        data-testid={`ordering-pin-${kind}-${item.id}`}
        aria-pressed={!!item.pinned}
        title={item.pinned ? "Unpin from top" : "Pin to top"}
        className={`p-2 transition-colors flex-shrink-0 ${
          item.pinned ? "text-[#A87900]" : "text-[#999] hover:text-[#A87900]"
        }`}
      >
        <Star className="w-5 h-5" strokeWidth={1.5} fill={item.pinned ? "#F4C430" : "none"} />
      </button>
    </div>
  );
}

function OrderingList({ kind, items, setItems, onSave, saving }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    setItems(arrayMove(items, oldIndex, newIndex));
  };
  const togglePin = (id) =>
    setItems(items.map((i) => (i.id === id ? { ...i, pinned: !i.pinned } : i)));

  const pinnedCount = items.filter((i) => i.pinned).length;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="text-xs text-[#555]">
          {items.length} {kind === "artwork" ? "works" : "artists"} · {pinnedCount} pinned to top ·
          drag to set the exact order visitors see
        </p>
        <Button
          onClick={onSave}
          disabled={saving}
          data-testid={`ordering-save-${kind}`}
          className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-6 py-3 h-auto label-eyebrow"
        >
          {saving ? "Saving…" : "Save order"}
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="label-eyebrow text-[#555] py-8 text-center border border-dashed border-[#E5E0D8]">
          Nothing to arrange yet.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div data-testid={`ordering-list-${kind}`} className="space-y-2">
              {items.map((it) => (
                <SortableRow key={it.id} item={it} kind={kind} onTogglePin={togglePin} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

export default function AdminOrdering() {
  const [tab, setTab] = useState("artworks");
  const [artworks, setArtworks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingArt, setSavingArt] = useState(false);
  const [savingArtist, setSavingArtist] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [aw, ar] = await Promise.all([
          api.get("/admin/ordering/artworks"),
          api.get("/admin/ordering/artists"),
        ]);
        setArtworks(aw.data || []);
        setArtists(ar.data || []);
      } catch (err) {
        logger.error("Ordering load failed:", err);
        toast.error("Could not load the arrangement data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveArtworks = async () => {
    setSavingArt(true);
    try {
      await api.patch("/admin/ordering/artworks", {
        items: artworks.map((a) => ({ id: a.id, pinned: !!a.pinned })),
      });
      toast.success("Artwork order saved · live on the site");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingArt(false);
    }
  };

  const saveArtists = async () => {
    setSavingArtist(true);
    try {
      await api.patch("/admin/ordering/artists", {
        items: artists.map((a) => ({ id: a.id, pinned: !!a.pinned })),
      });
      toast.success("Artist order saved · live on the site");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingArtist(false);
    }
  };

  return (
    <div data-testid="admin-ordering-page">
      <p className="label-eyebrow text-[#1E3A8A]">— Display order</p>
      <h1 className="font-serif-display text-5xl tracking-tighter font-medium mt-3 text-[#111]">
        Arrangement
      </h1>
      <p className="text-[#555] mt-3 max-w-2xl">
        Drag to set the exact order visitors see in the Gallery, every category page and the Artists
        directory. Tap the star to pin an item to the very top of every listing it appears in.
        New artists and works always land at the bottom — nothing jumps to the top on its own.
      </p>

      <div className="mt-8 flex items-center gap-2 border-b border-[#E5E0D8]">
        <button
          data-testid="ordering-tab-artworks"
          onClick={() => setTab("artworks")}
          className={`label-eyebrow px-5 py-3 -mb-px border-b-2 transition-colors ${
            tab === "artworks" ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#555] hover:text-[#111]"
          }`}
        >
          Artworks
        </button>
        <button
          data-testid="ordering-tab-artists"
          onClick={() => setTab("artists")}
          className={`label-eyebrow px-5 py-3 -mb-px border-b-2 transition-colors ${
            tab === "artists" ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#555] hover:text-[#111]"
          }`}
        >
          Artists
        </button>
      </div>

      <div className="mt-8">
        {loading ? (
          <p className="label-eyebrow text-[#555]">Loading arrangement…</p>
        ) : tab === "artworks" ? (
          <OrderingList kind="artwork" items={artworks} setItems={setArtworks} onSave={saveArtworks} saving={savingArt} />
        ) : (
          <OrderingList kind="artist" items={artists} setItems={setArtists} onSave={saveArtists} saving={savingArtist} />
        )}
      </div>
    </div>
  );
}
